import { useState } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useReferralTracking } from '@/contexts/ReferralTrackingContext';

interface Job {
  id: string;
  title: string;
  description: string;
  location: string;
  employment_type: string;
  salary_min?: number;
  salary_max?: number;
  currency?: string;
  created_at: string;
  updated_at: string;
  user_id: string;
  status: string;
  has_ai_interviewer: boolean;
  view_count?: number;
  application_steps?: Array<{
    id: string;
    type: string;
    title: string;
    description?: string;
  }>;
  company_profiles: {
    name: string;
    logo_url?: string;
    company_identifier?: string;
    description?: string;
    industry?: string;
    website?: string;
  } | null;
}

export function useJobDialogFromNotification() {
  const [job, setJob] = useState<Job | null>(null);
  const [loading, setLoading] = useState(false);
  const [dialogOpen, setDialogOpen] = useState(false);
  const { setReferralData } = useReferralTracking();

  const openJobFromNotification = async (notificationData: any) => {
    setLoading(true);
    
    try {
      const {
        job_id,
        company_id,
        recruiter_id,
        recruiter_name,
        job_title,
        company_name
      } = notificationData;

      // Set referral data for the recruiter invitation
      if (recruiter_id) {
        setReferralData({
          recruiter_id: String(recruiter_id),
          referral_source: 'recruiter_invitation',
          timestamp: Date.now().toString(),
          original_job_id: String(job_id),
          company_id: String(company_id || ''),
          expires_at: String(Date.now() + (90 * 24 * 60 * 60 * 1000)) // 90 days
        });
      }

      // Fetch the full job details
      const { data: jobData, error } = await supabase
        .from('company_job_postings')
        .select(`
          *,
          company_profiles (
            name,
            logo_url,
            company_identifier,
            description,
            industry,
            website
          )
        `)
        .eq('id', job_id)
        .single();

      if (error) {
        console.error('Error fetching job details:', error);
        return;
      }

      if (jobData) {
        const formattedJob: Job = {
          id: (jobData as any).id,
          title: (jobData as any).title,
          description: (jobData as any).description || '',
          location: (jobData as any).location || '',
          employment_type: (jobData as any).employment_type || '',
          salary_min: (jobData as any).salary_min,
          salary_max: (jobData as any).salary_max,
          currency: (jobData as any).currency,
          created_at: (jobData as any).created_at || '',
          updated_at: (jobData as any).updated_at || '',
          user_id: (jobData as any).user_id,
          status: (jobData as any).status || '',
          has_ai_interviewer: (jobData as any).job_ai_interviewer_enabled || false,
          view_count: (jobData as any).view_count,
          company_profiles: (jobData as any).company_profiles
        };
        
        setJob(formattedJob);
        setDialogOpen(true);
      }
    } catch (error) {
      console.error('Error opening job from notification:', error);
    } finally {
      setLoading(false);
    }
  };

  const closeDialog = () => {
    setDialogOpen(false);
    setJob(null);
  };

  return {
    job,
    loading,
    dialogOpen,
    openJobFromNotification,
    closeDialog
  };
}